/**
 * King Living override: default variant `full` so background Video/Image and scrollable entries resolve.
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseMediaWithScrollableTextById,
  getBaseMediaWithScrollableTexts,
  getFullMediaWithScrollableTextById,
  getFullMediaWithScrollableTexts,
  getMediaWithScrollableTextById,
  getMediaWithScrollableTexts,
} from '@amblique/contentful/app/contentful/queries';

export function useMediaWithScrollableTextById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query =
    variant === 'base' ?
      getBaseMediaWithScrollableTextById :
      variant === 'full' ?
        getFullMediaWithScrollableTextById :
        getMediaWithScrollableTextById;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'mediaWithScrollableText',
      'id',
      id,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],
    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(print(query), {
        ...variables,
        id,
        locale: contentfulService.locale,
      }, requestOptions);

      return data?.mediaWithScrollableText || null;
    },
    enabled: Boolean(contentfulService && id),
    ...queryOptions,
  });
}

export function useMediaWithScrollableTexts(options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query =
    variant === 'base' ?
      getBaseMediaWithScrollableTexts :
      variant === 'full' ?
        getFullMediaWithScrollableTexts :
        getMediaWithScrollableTexts;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'mediaWithScrollableText',
      'collection',
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],
    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(print(query), {
        ...variables,
        locale: contentfulService.locale,
      }, requestOptions);

      return data?.mediaWithScrollableTextCollection || {total: 0, skip: 0, limit: 0, items: []};
    },
    enabled: Boolean(contentfulService),
    ...queryOptions,
  });
}
